// EXPERIMENT: psf_featured_spiking_petition_experiment — remove with experiment cleanup
import { useCallback, useEffect, useMemo, useState } from 'react';

import qs from 'qs';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { useCountryCode } from '#src/app/shared/hooks/l10n';

import type { FeaturedSpikingPetitionArm } from '../useFeaturedSpikingPetitionExperiment';
import { claimFeaturedTrigger, isFeaturedTriggerUsed } from '../useFeaturedSpikingPetitionExperiment/featuredTriggers';

/**
 * In `v1`, Skip goes to the featured petition page, which continues to `skipUrl`. In `fake_control`, Skip
 * fires `featured_psf_step_would_have_seen`, so the analysis can compare `v1` with the `fake_control` signers
 * who would have seen the step. Either acts on the first Skip of a page load only (`featuredTriggers.ts`).
 */
export function useFeaturedSpikingPetitionSkip(
	skipUrl: string,
	arm: FeaturedSpikingPetitionArm | null,
): ModelHookResult<{ skipHref: string }, { handleFeaturedSkipClick: () => void }> {
	const {
		data: {
			petition: { slug },
			featuredSpikingPetition,
		},
	} = usePsfPromoteOrSharePageData();
	const track = useTracking();
	const countryCode = useCountryCode();
	// Starts from the latch, so a remount after a Skip keeps the normal next step
	const [isSkipUsed, setSkipUsed] = useState(() => isFeaturedTriggerUsed('skip'));

	// After a Skip, Back can restore this page from the back-forward cache, which does not re-render it. The
	// href changes then, not at the click: React commits a click's state update before the browser follows
	// the link, so the first Skip would go to `skipUrl`.
	useEffect(() => {
		if (arm !== 'v1') return undefined;
		const onPageShow = (event: PageTransitionEvent) => {
			if (event.persisted && isFeaturedTriggerUsed('skip')) setSkipUsed(true);
		};
		window.addEventListener('pageshow', onPageShow);
		return () => window.removeEventListener('pageshow', onPageShow);
	}, [arm]);

	const skipHref = useMemo(
		() =>
			arm === 'v1' && featuredSpikingPetition && !isSkipUsed
				? `/p/${encodeURIComponent(slug)}/psf/featured-petition${qs.stringify({ next: skipUrl }, { addQueryPrefix: true })}`
				: skipUrl,
		[arm, featuredSpikingPetition, isSkipUsed, slug, skipUrl],
	);

	const trackWouldHaveSeen = useCallback(() => {
		if (!featuredSpikingPetition) return;
		// The same properties as the step's events. The combo page adds the host `petition_id`.
		void track('featured_psf_step_would_have_seen', {
			featured_petition_id: `petition_${featuredSpikingPetition.id}`,
			featured_psf_step_variation: 'fake_control',
			user_country_code: countryCode,
			context: 'combo_psf_skip',
		});
	}, [featuredSpikingPetition, track, countryCode]);

	const handleFeaturedSkipClick = useCallback(() => {
		if (!featuredSpikingPetition || (arm !== 'v1' && arm !== 'fake_control')) return;
		if (claimFeaturedTrigger('skip') && arm === 'fake_control') trackWouldHaveSeen();
	}, [arm, featuredSpikingPetition, trackWouldHaveSeen]);

	return { data: { skipHref }, actions: { handleFeaturedSkipClick } };
}
